import api from './api';

// import stockLogger from '../utils/StockLogger.js';

/**
 * Servico para gerenciar consultas de estoque do Bling
 */
class BlingStockService {
    constructor() {
        this.cache = new Map();
        this.cacheTimeout = 5 * 60 * 1000; // 5 minutos
        this.pendingRequests = new Map();
        this.debounceTimeout = null;
        this.debounceDelay = 300; // 300ms

        // Flag para bloquear requisicoes quando ha erro de autenticacao
        this.isBlocked = false;
        this.blockReason = null;

        // Metricas de performance
        this.metrics = {
            totalRequests: 0,
            cacheHits: 0,
            cacheMisses: 0,
            errors: 0,
            averageResponseTime: 0,
            responseTimes: [],
        };

        this.maxBatchSize = 200;
    }

    _normalizeSkuKey(value) {
        const raw = String(value || '').trim();
        if (!raw) {
            return '';
        }

        try {
            return decodeURIComponent(raw).toUpperCase();
        } catch {
            const escaped = raw.replace(/%(?![0-9A-Fa-f]{2})/g, '%25');
            try {
                return decodeURIComponent(escaped).toUpperCase();
            } catch {
                return raw.toUpperCase();
            }
        }
    }

    _unwrapPayload(response) {
        if (response && typeof response === 'object' && Object.hasOwn(response, 'data')) {
            return response.data;
        }
        return response;
    }

    /**
     * Busca estoque APENAS para produtos Bling
     * @param {Array} products - Lista de produtos com seller_sku (APENAS BLING)
     * @returns {Promise<Map>} Mapa sku -> dados de estoque
     */
    async getStockForProducts(products) {
        if (!products || products.length === 0) {
            return new Map();
        }

        const blingProducts = products.filter((p) => !this._isMlProduct(p));
        const sellerSkus = this._extractSellerSkus(blingProducts);
        if (sellerSkus.length === 0) {
            return new Map();
        }
        return this._callBackend(sellerSkus);
    }

    /**
     * Atualiza dados de estoque para SKUs especificos
     * @param {Array} skus - Lista de SKUs para atualizar
     * @returns {Promise<Map>} Dados atualizados
     */
    async refreshStock(skus) {
        if (!skus || skus.length === 0) {
            return new Map();
        }

        return this._callBackend(skus);
    }

    /**
     * Limpa todo o cache
     */
    clearCache() {
        this.cache.clear();
        // console.log(' BlingStock: Cache limpo');
    }

    /**
     * Desbloqueia o servico apos reautenticacao bem-sucedida
     */
    unblock() {
        this.isBlocked = false;
        this.blockReason = null;
        console.log(' BlingStock: Servico desbloqueado');
    }

    /**
     * Verifica se o servico esta bloqueado
     */
    getBlockStatus() {
        return {
            isBlocked: this.isBlocked,
            reason: this.blockReason,
        };
    }

    /**
     * Extrai seller_skus unicos dos produtos
     * @param {Array} products - Lista de produtos
     * @returns {Array} Lista de SKUs unicos e validos
     */
    _extractSellerSkus(products) {
        const skus = new Set();

        products.forEach((product, index) => {
            if (!product || typeof product !== 'object') {
                return;
            }

            // Verificar diferentes possiveis localizacoes do seller_sku
            let sellerSku = null;

            // Verificar propriedades diretas
            if (product.seller_sku) {
                sellerSku = product.seller_sku;
            } else if (product.sellerSku) {
                sellerSku = product.sellerSku;
            } else if (product.sku) {
                sellerSku = product.sku;
            }
            // Verificar item_seller_sku (vem do banco de dados)
            else if (product.item_seller_sku) {
                sellerSku = product.item_seller_sku;
            }
            // Verificar estruturas aninhadas comuns
            else if (product.item && product.item.seller_sku) {
                sellerSku = product.item.seller_sku;
            } else if (product.item && product.item.item_seller_sku) {
                sellerSku = product.item.item_seller_sku;
            } else if (product.listing && product.listing.seller_sku) {
                sellerSku = product.listing.seller_sku;
            } else if (product.variation && product.variation.seller_sku) {
                sellerSku = product.variation.seller_sku;
            } else if (product.attributes && product.attributes.seller_sku) {
                sellerSku = product.attributes.seller_sku;
            }

            // Debug: Log da estrutura do produto para entender os dados
            // if (index < 5) { // Sempre mostrar os primeiros 5 produtos
            //   console.log(` [BlingStock] Produto ${index}:`, {
            //     title: product.title || product.item_title || 'N/A',
            //     seller_sku: product.seller_sku,
            //     sellerSku: product.sellerSku,
            //     item_seller_sku: product.item_seller_sku,
            //     sku: product.sku,
            //     itemId: product.itemId || product.item_id,
            //     productKeys: Object.keys(product).slice(0, 10),
            //     extractedSellerSku: sellerSku,
            //     hasSellerSku: !!sellerSku
            //   });
            // }

            // Log de aviso se nao encontrou SKU
            if (!sellerSku && index < 10) {
                console.warn(` [BlingStock] Produto ${index} sem seller_sku:`, {
                    title: product.title || product.item_title,
                    availableFields: Object.keys(product),
                });
            }

            // Validar se o SKU e valido
            if (sellerSku && typeof sellerSku === 'string' && sellerSku.trim().length > 0) {
                skus.add(sellerSku.trim());
            }
        });

        const skuArray = Array.from(skus);
        // console.log(` [BlingStock] Extraidos ${skus.size} SKUs unicos de ${products.length} produtos`);
        // console.log(` [BlingStock] SKUs extraidos:`, skuArray);

        if (skuArray.length === 0) {
            console.error(` [BlingStock] NENHUM SKU EXTRAIDO! Verifique a estrutura dos produtos.`);
        }

        return Array.from(skus);
    }

    /**
     * Busca dados do cache para os SKUs fornecidos
     * @param {Array} skus - Lista de SKUs
     * @returns {Object} Dados em cache
     */
    _getCachedData(skus) {
        const cachedData = {};
        const now = Date.now();

        skus.forEach((sku) => {
            const cached = this.cache.get(sku);
            if (cached && now - cached.timestamp < this.cacheTimeout) {
                cachedData[sku] = cached.data;
            }
        });

        return cachedData;
    }

    /**
     * Busca dados expirados do cache como fallback
     * @param {Array} skus - Lista de SKUs
     * @returns {Object} Dados expirados do cache
     */
    _getStaleData(skus) {
        const staleData = {};

        skus.forEach((sku) => {
            const cached = this.cache.get(sku);
            if (cached) {
                // Marcar como dados expirados
                staleData[sku] = {
                    ...cached.data,
                    stale: true,
                };
            }
        });

        return staleData;
    }

    /**
     * Faz requisicao para buscar dados de estoque
     * @param {Array} skus - Lista de SKUs
     * @returns {Promise<Object>} Dados de estoque
     */
    async _fetchStockData(skus) {
        // Verificar se ja ha uma requisicao pendente para estes SKUs
        const requestKey = skus.sort().join(',');

        if (this.pendingRequests.has(requestKey)) {
            // console.log(' BlingStock: Aguardando requisicao pendente');
            return this.pendingRequests.get(requestKey);
        }

        // Criar nova requisicao
        const requestPromise = this._makeStockRequest(skus);
        this.pendingRequests.set(requestKey, requestPromise);

        try {
            const result = await requestPromise;
            return result;
        } finally {
            // Limpar requisicao pendente
            this.pendingRequests.delete(requestKey);
        }
    }

    /**
     * Faz a requisicao HTTP para a API com retry automatico
     * @param {Array} skus - Lista de SKUs
     * @returns {Promise<Object>} Resposta da API
     */
    async _makeStockRequest(skus) {
        // Verificar se o servico esta bloqueado
        if (this.isBlocked) {
            console.error(` [BlingStock] Servico bloqueado: ${this.blockReason}`);
            throw new Error(this.blockReason || 'Servico Bling bloqueado devido a erro de autenticacao');
        }

        console.log(` [BlingStock] Buscando estoque de ${skus.length} SKUs da API do Bling`);

        const maxRetries = 3;
        let lastError = null;

        for (let attempt = 1; attempt <= maxRetries; attempt++) {
            try {
                const response = await api.post('/stock/batch', {
                    seller_skus: skus,
                });
                const payload = this._unwrapPayload(response);
                if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                    const receivedSkus = Object.keys(payload);
                    console.log(` [BlingStock] Dados recebidos da API do Bling para ${receivedSkus.length} produtos`);

                    // Verificar fonte dos dados
                    const blingData = Object.values(payload).filter((d) => d.source === 'bling_api').length;
                    const mlData = Object.values(payload).filter((d) => d.source === 'mercadolivre_api').length;

                    if (mlData > 0) {
                        console.warn(
                            ` [BlingStock] ATENCAO: ${mlData} produtos vieram da API do ML! Isso nao deveria acontecer.`,
                        );
                    }

                    console.log(` [BlingStock] Fonte dos dados: ${blingData} Bling, ${mlData} ML`);

                    // Verificar quais SKUs foram encontrados e quais nao
                    const notFound = skus.filter((sku) => !receivedSkus.includes(sku));
                    if (notFound.length > 0) {
                        console.warn(` [BlingStock] SKUs nao encontrados no Bling:`, notFound);
                    }

                    // Mostrar detalhes dos estoques retornados
                    // receivedSkus.forEach(sku => {
                    //   const stock = response.data[sku];
                    //   console.log(` [BlingStock] ${sku}:`, {
                    //     quantity: stock.quantity || stock,
                    //     status: stock.status,
                    //     warehouses: stock.warehouses?.length || 0
                    //   });
                    // });

                    // console.log(' [BlingStock] Dados recebidos da API:', response.data);
                    // console.log(' [BlingStock] Chaves dos dados:', Object.keys(response.data));
                    return payload;
                }
                throw new Error('Resposta da API indica falha');
            } catch (error) {
                lastError = error;

                // Verificar se e erro de reautenticacao necessaria
                const isReauthError =
                    error.code === 'REAUTH_REQUIRED' ||
                    error.status === 401 ||
                    (error.response && error.response.error && error.response.error.code === 'REAUTH_REQUIRED');

                if (isReauthError) {
                    // Bloquear o servico para evitar requisicoes futuras
                    this.isBlocked = true;
                    this.blockReason =
                        error.message ||
                        error.response?.error?.message ||
                        'Erro de autenticacao com o Bling. Reautenticacao necessaria.';
                    console.error(
                        ' BlingStock: Erro de autenticacao detectado. Bloqueando requisicoes futuras:',
                        this.blockReason,
                    );
                    throw new Error(this.blockReason);
                }

                // Se e erro de token expirado, nao fazer retry
                if (error.code === 'TOKEN_EXPIRED' || error.status === 401) {
                    console.error(' BlingStock: Token expirado, parando tentativas');
                    throw error;
                }

                // Se e o ultimo attempt, nao fazer retry
                if (attempt === maxRetries) {
                    break;
                }

                // Calcular delay com backoff exponencial
                const delay = Math.min(1000 * 2 ** (attempt - 1), 8000); // Max 8s

                console.warn(
                    ` BlingStock: Tentativa ${attempt}/${maxRetries} falhou, tentando novamente em ${delay}ms`,
                );
                console.warn(`   Erro:`, error.message);

                // Aguardar antes do proximo attempt
                await new Promise((resolve) => setTimeout(resolve, delay));
            }
        }

        // Se chegou aqui, todas as tentativas falharam
        console.error(` BlingStock: Todas as ${maxRetries} tentativas falharam`);
        throw lastError;
    }

    /**
     * Atualiza o cache com novos dados
     * @param {Object} data - Dados para armazenar
     */
    _updateCache(data) {
        const timestamp = Date.now();

        Object.entries(data).forEach(([sku, stockData]) => {
            this.cache.set(sku, {
                data: stockData,
                timestamp: timestamp,
            });
        });
    }

    /**
     * Cria mapa de erro para SKUs que falharam
     * @param {Array} skus - Lista de SKUs
     * @returns {Map} Mapa de erro
     */
    _createErrorMap(skus) {
        const errorMap = new Map();

        skus.forEach((sku) => {
            errorMap.set(sku, {
                quantity: 0,
                status: 'error',
                lastUpdated: new Date().toISOString(),
            });
        });

        return errorMap;
    }

    /**
     * Registra tempo de resposta para metricas
     * @param {number} startTime - Timestamp de inicio
     */
    _recordResponseTime(startTime) {
        const responseTime = Date.now() - startTime;
        this.metrics.responseTimes.push(responseTime);

        // Manter apenas os ultimos 100 tempos de resposta
        if (this.metrics.responseTimes.length > 100) {
            this.metrics.responseTimes.shift();
        }

        // Calcular media
        this.metrics.averageResponseTime =
            this.metrics.responseTimes.reduce((sum, time) => sum + time, 0) / this.metrics.responseTimes.length;
    }

    /**
     * Retorna estatisticas do cache
     * @returns {Object} Estatisticas
     */
    getCacheStats() {
        const now = Date.now();
        let validEntries = 0;
        let expiredEntries = 0;

        this.cache.forEach((entry) => {
            if (now - entry.timestamp < this.cacheTimeout) {
                validEntries++;
            } else {
                expiredEntries++;
            }
        });

        return {
            totalEntries: this.cache.size,
            validEntries,
            expiredEntries,
            cacheTimeout: this.cacheTimeout,
        };
    }

    /**
     * Retorna metricas de performance
     * @returns {Object} Metricas
     */
    getPerformanceMetrics() {
        const cacheStats = this.getCacheStats();
        const hitRate =
            this.metrics.totalRequests > 0
                ? (this.metrics.cacheHits / (this.metrics.cacheHits + this.metrics.cacheMisses)) * 100
                : 0;

        return {
            ...this.metrics,
            cacheHitRate: hitRate,
            cacheStats,
            requestsPerMinute: this._calculateRequestsPerMinute(),
        };
    }

    /**
     * Calcula requisicoes por minuto (ultimos 5 minutos)
     */
    _calculateRequestsPerMinute() {
        // Implementacao simplificada - em producao usaria timestamps mais precisos
        return this.metrics.totalRequests / 5; // Aproximacao
    }

    /**
     * Busca todos os produtos do Bling
     * @returns {Promise<Array>} Array de produtos
     */
    async getProducts() {
        try {
            const limit = this.maxBatchSize;
            let offset = 0;
            let total = null;
            let safetyCounter = 0;
            const allRows = [];

            while (safetyCounter < 200) {
                safetyCounter += 1;

                const response = await api.get(`/stock/products?limit=${limit}&offset=${offset}`);
                const rows = Array.isArray(response?.results)
                    ? response.results
                    : Array.isArray(response?.products)
                      ? response.products
                      : Array.isArray(response?.data)
                        ? response.data
                        : Array.isArray(response)
                          ? response
                          : [];

                const paging = response?.paging || null;
                const pagingTotal = Number(paging?.total);

                if (Number.isFinite(pagingTotal) && pagingTotal >= 0) {
                    total = pagingTotal;
                }

                if (rows.length === 0) {
                    break;
                }

                allRows.push(...rows);
                offset += rows.length;

                if (total !== null && offset >= total) {
                    break;
                }

                if (rows.length < limit) {
                    break;
                }
            }

            return allRows;
        } catch (error) {
            console.error(' [BlingStock] Erro ao buscar produtos:', error);
            throw error;
        }
    }

    /**
     * Busca todos os depositos do Bling
     * @returns {Promise<Array>} Array de depositos
     */
    async getWarehouses() {
        try {
            // console.log(' [BlingStock] Buscando depositos do Bling...');

            const response = await api.get('/stock/warehouses');
            const rows = Array.isArray(response?.results)
                ? response.results
                : Array.isArray(response?.warehouses)
                  ? response.warehouses
                  : Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response)
                      ? response
                      : [];

            if (rows.length >= 0) {
                return rows;
            }

            throw new Error('Resposta invalida da API');
        } catch (error) {
            console.error(' [BlingStock] Erro ao buscar depositos:', error);
            throw error;
        }
    }

    /**
     * Busca todos os dados de estoque do Bling
     * Envia array vazio para o backend retornar TODOS os SKUs
     * @returns {Promise<Map>} Map com SKU -> dados de estoque
     */
    async getAllStockData() {
        try {
            // console.log(' [BlingStock] Buscando todos os estoques...');

            // Array vazio = backend retorna TODOS os SKUs
            const response = await api.post('/stock/batch', {
                seller_skus: [],
            });
            const payload = this._unwrapPayload(response);
            if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
                // console.log(` [BlingStock] ${Object.keys(response.data).length} estoques encontrados`);

                // Converter para Map
                const stockMap = new Map();
                Object.entries(payload).forEach(([sku, stockInfo]) => {
                    stockMap.set(sku, stockInfo);
                });

                return stockMap;
            }

            throw new Error('Resposta invalida da API');
        } catch (error) {
            console.error(' [BlingStock] Erro ao buscar estoques:', error);
            throw error;
        }
    }

    /**
     * Busca todos os produtos do Bling
     * @returns {Promise<Array>} Lista de produtos com estoque
     */
    async getAllProducts() {
        try {
            // console.log(' [BlingStock] Buscando todos os produtos...');

            const response = await api.get('/stock/products');
            const rows = Array.isArray(response?.results)
                ? response.results
                : Array.isArray(response?.products)
                  ? response.products
                  : Array.isArray(response?.data)
                    ? response.data
                    : Array.isArray(response)
                      ? response
                      : [];

            return this._formatProductsData(rows);
        } catch (error) {
            console.error(' [BlingStock] Erro ao buscar produtos:', error);
            throw error;
        }
    }

    /**
     * Busca produto especifico por SKU
     * @param {string} sku - SKU do produto
     * @returns {Promise<Object>} Dados do produto
     */
    async searchBySku(sku) {
        try {
            // console.log(` [BlingStock] Buscando produto por SKU: ${sku}`);

            const response = await api.get(`/stock/products/${sku}`);
            const payload = this._unwrapPayload(response);
            if (payload && typeof payload === 'object') {
                // console.log(` [BlingStock] Produto encontrado:`, response.data);
                return this._formatProductData(payload);
            }

            return null;
        } catch (error) {
            console.error(` [BlingStock] Erro ao buscar produto ${sku}:`, error);
            throw error;
        }
    }

    /**
     * Verifica se um produto e do Mercado Livre
     * @param {Object} product - Produto a verificar
     * @returns {boolean} true se for produto ML
     */
    _isMlProduct(product) {
        // Produto ML tem itemId
        return !!(product.itemId || product.item_id || product.mlItemId);
    }

    /**
     * Formata dados de multiplos produtos
     * @param {Array} products - Lista de produtos
     * @returns {Array} Produtos formatados
     */
    _formatProductsData(products) {
        return products.map((product) => this._formatProductData(product));
    }

    /**
     * Formata dados de um produto
     * @param {Object} product - Dados do produto
     * @returns {Object} Produto formatado
     */
    _formatProductData(product) {
        // console.log(' [BlingStock] Formatando produto:', product);

        // Calcular estoque total somando todos os depositos
        const warehouses = product.warehouses || product.estoques || product.depositos || [];
        const totalStock = warehouses.reduce((sum, w) => {
            const qty = w.quantity || w.quantidade || w.saldo || w.estoque || 0;
            return sum + qty;
        }, 0);

        // console.log(' [BlingStock] Estoque calculado:', {
        //   sku: product.sku || product.codigo,
        //   warehouses: warehouses.length,
        //   totalStock
        // });

        return {
            sku: product.sku || product.codigo,
            code: product.codigo || product.sku,
            name: product.nome || product.descricao || product.name,
            totalStock,
            warehouses: warehouses.map((w) => ({
                name: w.name || w.nome || w.deposito,
                description: w.description || w.descricao,
                quantity: w.quantity || w.quantidade || w.saldo || w.estoque || 0,
            })),
            ...product,
        };
    }

    /**
     * Consulta o backend /api/v2/stock/batch (cache em memoria no servidor)
     */
    async _callBackend(sellerSkus) {
        try {
            const uniqueSkus = [...new Set((sellerSkus || []).map((sku) => String(sku || '').trim()).filter(Boolean))];
            if (uniqueSkus.length === 0) {
                return new Map();
            }

            const map = new Map();
            for (let index = 0; index < uniqueSkus.length; index += this.maxBatchSize) {
                const batch = uniqueSkus.slice(index, index + this.maxBatchSize);
                const response = await api.post('/stock/batch', { seller_skus: batch });
                const payload = this._unwrapPayload(response);
                if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
                    continue;
                }

                Object.entries(payload).forEach(([sku, entry]) => {
                    const quantity = Number(entry?.quantity || 0);
                    const normalizedSku = this._normalizeSkuKey(sku);
                    const stockPayload = {
                        quantity,
                        price: entry?.price !== null && entry?.price !== undefined ? Number(entry.price) : 0,
                        costPrice:
                            entry?.costPrice !== null && entry?.costPrice !== undefined ? Number(entry.costPrice) : 0,
                        brand: entry?.brand || null,
                        status: entry?.status || (quantity > 0 ? 'available' : 'out-of-stock'),
                        source: entry?.source || 'inventory_cache',
                        lastUpdated: entry?.lastUpdated || new Date().toISOString(),
                        warehouses: Array.isArray(entry?.warehouses) ? entry.warehouses : [],
                    };
                    map.set(sku, stockPayload);
                    if (normalizedSku) {
                        map.set(normalizedSku, stockPayload);
                    }
                });
            }

            for (const rawSku of uniqueSkus) {
                if (map.has(rawSku)) {
                    continue;
                }
                const normalized = this._normalizeSkuKey(rawSku);
                if (normalized && map.has(normalized)) {
                    map.set(rawSku, map.get(normalized));
                }
            }

            return map;
        } catch (error) {
            console.error(' BlingStock: Erro ao consultar cache do backend:', error);
            return this._createErrorMap(sellerSkus);
        }
    }
}

// Singleton
const blingStockService = new BlingStockService();

export default blingStockService;
